import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react"
import styles from "./SearchSelect.module.scss"
import { useTranslation } from "react-i18next"
import _debounce from "lodash/debounce"
import { GetUserAvatar } from "../Assets/GetUserAvatar/GetUserAvatar"
import { CrossIcon as OurCrossIcon } from "../../icons/CrossIcon"
import clsx from "clsx"
import { IEmployee } from "../../types/content"
import { selectUser } from "../../redux/slice/auth"
import { useAppSelector } from "../../hooks"
import { v4 as uuidv4 } from "uuid"
import { useLazyGetUsersByProjectsQuery } from "../../redux/api/content"

interface Props {
  className?: string
  setSelectedVal: Dispatch<SetStateAction<{ project_id: string; project_name: string; user: IEmployee } | null>>
  selectedVal?: { project_id: string; project_name: string; user: IEmployee } | null
  isShortClick?: (userID: string, name: string, projectID: string) => void
  isShort?: boolean
}

const LIMIT_REQUEST = 20

const SearchSelect: React.FC<Props> = ({ className, setSelectedVal, selectedVal, isShortClick, isShort }) => {
  const { t } = useTranslation("translation", { keyPrefix: `interface` })
  const user = useAppSelector(selectUser)

  const [getUsersByProjects, { isFetching, isLoading, isUninitialized }] = useLazyGetUsersByProjectsQuery()

  const listContainerRef = useRef<HTMLDivElement>(null)
  const loaderRef = useRef<HTMLDivElement>(null)
  const [isSearchLoading, setSearchLoading] = useState<boolean>(false)
  const [isListEnd, setListEnd] = useState<boolean>(false)
  const [viewList, setViewList] = useState(false)
  const [isNotSearching, setIsNotSearching] = useState(false)
  const [viewSkeleton, setViewSkeleton] = useState(false)
  const [offset, setOffset] = useState<number>(0)
  const [searchVal, setSearchVal] = useState<string>()
  const [items, setItems] = useState<{ project_id: string; project_name: string; users: IEmployee[] }[]>([])

  const fetchData = async (isStart?: boolean, str?: string) => {
    if (isListEnd) return
    setOffset((prev) => (isStart ? 0 : prev + LIMIT_REQUEST))
    await getUsersByProjects({ limit: LIMIT_REQUEST, search: str ?? "", offset: isStart ? 0 : offset + LIMIT_REQUEST })
      .unwrap()
      .then((res) => {
        setItems((prevItems) => {
          const itemsMap = new Map(prevItems.map((item) => [item.project_id, item]))
          res.data.forEach((p) => {
            if (itemsMap.has(p.project_id)) {
              const existing = itemsMap.get(p.project_id)
              if (existing) {
                itemsMap.set(p.project_id, {
                  ...existing,
                  users: [...existing.users, ...p.users],
                })
              }
            } else {
              itemsMap.set(p.project_id, p)
            }
          })
          return Array.from(itemsMap.values())
        })
        setListEnd(res.bIsEnd)
        setViewSkeleton(false)
      })
      .catch((err) => console.error("error: ", err))
    setSearchLoading(false)
  }

  const debounceFn = useCallback(
    _debounce((str: string) => {
      setViewList(true)
      fetchData(true, str)
    }, 500),
    [isUninitialized],
  )

  const clearing = () => {
    setSelectedVal(null)
    setIsNotSearching(false)
    setSearchVal("")
    setOffset(0)
    setItems([])
    setListEnd(false)
    setViewList(false)
  }

  const list = useMemo(() => {
    if (!items?.length) return null
    return items.map((proj) => {
      if (proj.users.length === 1 && proj.users[0].id === user?.id) return null
      return (
        <div key={proj.project_id}>
          <h5 className={styles.projectTitle}>{proj.project_name}</h5>
          <div className={styles.grid}>
            {proj.users.map((employee, i: number) => {
              if (employee.id === user?.id) return null
              const idE = uuidv4()
              return (
                <div
                  className={styles["select-item"]}
                  key={i + idE}
                  onClick={() => {
                    if (isShortClick) {
                      isShortClick(employee.id, employee.name, proj.project_id)
                      return
                    }
                    setSelectedVal({ project_id: proj.project_id, project_name: proj.project_name, user: employee })
                    setSearchVal(employee.name)
                    setIsNotSearching(true)
                    setViewList(false)
                  }}
                >
                  <GetUserAvatar
                    avatar={employee.avatar_id}
                    name={employee.name}
                    className={styles["select-item__avatar"]}
                  />
                  <div className={styles["select-item__info"]}>
                    <span className={styles["select-item__label"]}>{employee.name}</span>
                    {employee.position && <span className={styles["select-item__position"]}>{employee.position}</span>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )
    })
  }, [items, user?.id])

  useEffect(() => {
    if (isUninitialized) return

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0]
        if (target.isIntersecting) {
          if (isFetching || isSearchLoading) return

          void fetchData(false, searchVal ?? "")
        }
      },
      { root: listContainerRef.current },
    )

    if (loaderRef.current) {
      observer.observe(loaderRef.current)
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current)
        observer.disconnect()
      }
    }
  }, [isUninitialized, isLoading, isSearchLoading, items, isFetching, listContainerRef, searchVal])

  useEffect(() => {
    if (searchVal?.length === 0) {
      clearing()
    }
    if (!searchVal || searchVal?.length < 2) return
    if (isNotSearching) {
      setIsNotSearching(false)
      return
    }
    if (searchVal) {
      setOffset(0)
      setSearchLoading(true)
      setItems([])

      setListEnd(false)
      setViewSkeleton(true)
      debounceFn(searchVal)
    }
  }, [searchVal])

  return (
    <div className={clsx(styles["select"], { [styles["select__shorted"]]: isShortClick }, className)}>
      <div className={styles["input-container"]}>
        <input
          onChange={(e) => {
            if (e.target) setSearchVal(e.target.value)
          }}
          value={searchVal}
          placeholder={t("search")}
          name={"input-search-select"}
        />
        <div className={styles.indicatorsContainer}>
          {selectedVal?.user?.id && (
            <div
              className={clsx(styles["indicatorsContainer__icon-container"], styles["close-icon"])}
              onClick={() => {
                clearing()
              }}
            >
              <OurCrossIcon />
            </div>
          )}
          <div className={clsx(styles["indicatorsContainer__icon-container"])}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667C8.56581 12.6667 9.70064 12.2486 10.6038 11.5466L12.862 13.8049C13.1224 14.0652 13.5445 14.0652 13.8049 13.8049C14.0652 13.5445 14.0652 13.1224 13.8049 12.862L11.5466 10.6038C12.2486 9.70064 12.6667 8.56581 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2ZM3.33333 7.33333C3.33333 5.12419 5.12419 3.33333 7.33333 3.33333C9.54247 3.33333 11.3333 5.12419 11.3333 7.33333C11.3333 9.54247 9.54247 11.3333 7.33333 11.3333C5.12419 11.3333 3.33333 9.54247 3.33333 7.33333Z"
              />
            </svg>
          </div>
        </div>
      </div>

      {viewList && (
        <div
          className={clsx(styles["list-container"], {
            [styles["list-container__clear"]]: items?.length === 0 && !viewSkeleton,
            [styles["list-container__shorted"]]: isShortClick,
          })}
          ref={listContainerRef}
        >
          {items?.length ? (
            list
          ) : (
            <>{isListEnd && !items.length && <div className={styles["not-found"]}>{t("noResults")}</div>}</>
          )}
          {!isListEnd && (
            <div ref={loaderRef} className={clsx("employees__grid", styles.grid)}>
              {(isSearchLoading || isFetching || viewSkeleton) &&
                [...Array(4)].map((_item, index) => (
                  <div key={index} className={clsx(styles.skeleton, "skeletonBlock")} />
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchSelect
